import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useMotionValue, useInView } from "framer-motion";

const ProgramStrategisCard = ({ data, index }) => {
  const [animationComplete, setAnimationComplete] = useState(false);
  const [bgAnimationComplete, setBgAnimationComplete] = useState(false);
  const [controls] = useState(useAnimation());
  const [bgControls] = useState(useAnimation());

  const opacity = useMotionValue("100%");
  const bgScale = useMotionValue("100%");

  const [isHovering, setIsHovering] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    controls
      .start({
        opacity: ["100%", "0%", "0%", "0%", "100%"],
        transition: {
          duration: 2,
          times: [0, 0.25, 0.5, 0.75, 1],
          delay: 2.25 + index * 0.25,
          ease: "easeInOut",
        },
      })
      .then(() => {
        setAnimationComplete(true);
        opacity.set("100%");
      });

    bgControls
      .start({
        scale: ["100%", "110%", "110%", "110%", "100%"],
        transition: {
          duration: 2,
          times: [0, 0.25, 0.5, 0.75, 1],
          delay: 2.25 + index * 0.25,
          ease: "easeInOut",
        },
      })
      .then(() => {
        setBgAnimationComplete(true);
        bgScale.set("100%");
      });
  }, [isInView, controls, opacity, bgControls, bgScale, index]);

  const handleHoverStart = async () => {
    if (
      animationComplete &&
      bgAnimationComplete &&
      !isHovering &&
      !isAnimating
    ) {
      setIsAnimating(true);
      setIsHovering(true);

      await Promise.all([
        controls.start({
          opacity: "0%",
          transition: {
            type: "spring",
            stiffness: 120,
            damping: 20,
          },
        }),
        bgControls.start({
          scale: "110%",
          transition: {
            type: "spring",
            stiffness: 120,
            damping: 20,
          },
        }),
      ]);

      setIsAnimating(false);
    }
  };

  const handleHoverEnd = async () => {
    if (!animationComplete || !bgAnimationComplete) return;

    controls.stop();
    bgControls.stop();

    setIsAnimating(true);
    setIsHovering(false);

    await Promise.all([
      controls.start({
        opacity: "100%",
        transition: {
          type: "spring",
          stiffness: 120,
          damping: 20,
        },
      }),
      bgControls.start({
        scale: "100%",
        transition: {
          type: "spring",
          stiffness: 120,
          damping: 20,
        },
      }),
    ]);

    setIsAnimating(false);
  };

  return (
    <motion.div
      ref={ref}
      className="relative flex justify-end items-center h-52 md:h-96 px-5 py-12 rounded-2xl overflow-hidden group"
      initial={{ y: 50, opacity: 0 }}
      whileInView={{
        y: 0,
        opacity: 1,
      }}
      transition={{
        duration: 0.5,
        delay: 1.25 + index * 0.25,
        ease: "easeInOut",
      }}
      viewport={{ once: true }}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
    >
      <motion.div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${data.image})` }}
        animate={bgControls}
      />
      <motion.div
        className="absolute inset-0 bg-[#000000]/30"
        animate={controls}
      />
      <motion.div
        className="flex justify-start items-end text-white text-lg sm:text-xl md:text-2xl font-bold w-full h-full z-10"
        animate={controls}
      >
        {data.title}
      </motion.div>
    </motion.div>
  );
};

export default ProgramStrategisCard;
